import { z } from "zod";
import { MEDICATION_FREQUENCIES } from "@shared/constants/frequency.js";
import { CHILD_NAME_MAX, NOTES_MAX } from "@shared/constants/validation.js";

const MEDICATION_NAME_MAX = CHILD_NAME_MAX;
const DOSAGE_MAX = 120;

const nullableTrimmedString = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => (v && v.length > 0 ? v : null));

const updateNullableString = (max: number) =>
  z.string().trim().max(max).nullable().optional();

// Accept ISO strings from the JSON body and coerce to Date.
// `null` is allowed (no expiration).
const expirationDateSchema = z
  .union([z.string().datetime({ offset: true }), z.date()])
  .nullish()
  .transform((v) => {
    if (v === null || v === undefined || v === "") return null;
    return v instanceof Date ? v : new Date(v);
  });

const updateExpirationDateSchema = z
  .union([z.string().datetime({ offset: true }), z.date(), z.null()])
  .optional()
  .transform((v) => {
    if (v === undefined) return undefined;
    if (v === null) return null;
    return v instanceof Date ? v : new Date(v);
  });

export const createMedicationSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "Medication name is required")
    .max(MEDICATION_NAME_MAX, "Medication name is too long"),
  dosage: z
    .string()
    .trim()
    .min(1, "Dosage is required")
    .max(DOSAGE_MAX, "Dosage is too long"),
  frequency: z.enum(MEDICATION_FREQUENCIES, {
    errorMap: () => ({ message: "Select a frequency" }),
  }),
  expiresAt: expirationDateSchema,
  notes: nullableTrimmedString(NOTES_MAX),
});

export const updateMedicationSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "Medication name is required")
    .max(MEDICATION_NAME_MAX)
    .optional(),
  dosage: z
    .string()
    .trim()
    .min(1, "Dosage is required")
    .max(DOSAGE_MAX)
    .optional(),
  frequency: z.enum(MEDICATION_FREQUENCIES).optional(),
  expiresAt: updateExpirationDateSchema,
  notes: updateNullableString(NOTES_MAX),
});

export type CreateMedicationInput = z.infer<typeof createMedicationSchema>;
export type UpdateMedicationInput = z.infer<typeof updateMedicationSchema>;

/**
 * Form-side schema used with zodResolver in MedicationDialog. The HTML
 * `<input type="date">` returns a `YYYY-MM-DD` string (or empty string), not
 * a Date. This schema validates the form shape; the dialog converts the
 * string to a Date before calling the create/update mutation.
 */
export const medicationFormSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "Medication name is required")
    .max(MEDICATION_NAME_MAX, "Medication name is too long"),
  dosage: z
    .string()
    .trim()
    .min(1, "Dosage is required")
    .max(DOSAGE_MAX, "Dosage is too long"),
  frequency: z.enum(MEDICATION_FREQUENCIES, {
    errorMap: () => ({ message: "Select a frequency" }),
  }),
  expiresAt: z
    .string()
    .refine((v) => !v || /^\d{4}-\d{2}-\d{2}$/.test(v), "Invalid date")
    .optional()
    .default(""),
  notes: z.string().max(NOTES_MAX).nullable().optional(),
});

export type MedicationFormValues = z.infer<typeof medicationFormSchema>;
